<template>
  <div class="calculator-page" :dir="isRTL ? 'rtl' : 'ltr'">
    <!-- Standalone Header (no IntercomLayout — public page) -->
    <header class="calc-header">
      <div class="calc-header-inner">
        <div class="calc-brand">
          <img src="/images/logo.png" alt="OnePlaceHQ" class="calc-logo" />
          <span class="calc-brand-text">{{ locale === 'ur_IN' ? 'فرائض' : 'Faraid' }}</span>
        </div>
        <div class="header-actions">
          <!-- Language Switcher -->
          <div class="lang-group">
            <span class="lang-hint">{{ $t('calculator.changeLanguage') }}</span>
            <div class="lang-switcher">
              <button
                v-for="lang in availableLocales"
                :key="lang.code"
                type="button"
                class="lang-btn"
                :class="{ active: currentLocale === lang.code }"
                :dir="lang.dir || 'ltr'"
                @click="switchLocale(lang.code)"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>
          <router-link v-if="!isAuthenticated" to="/login" class="login-link">
            {{ $t('login.signIn') }}
          </router-link>
          <router-link v-else to="/" class="login-link">
            {{ $t('nav.dashboard') }}
          </router-link>
        </div>
      </div>
    </header>

    <!-- Calculator Content -->
    <main class="calc-main">
      <div class="calc-container">
        <div class="calc-intro">
          <h1 class="calc-title">{{ $t('calculator.title') }}</h1>
          <p class="calc-subtitle">{{ $t('calculator.subtitle') }}</p>
        </div>

        <div class="calc-layout">
          <!-- Left: Input Panel -->
          <div class="calc-input-panel">
            <!-- Deceased Gender -->
            <div class="input-section">
              <label class="section-label">{{ $t('calculator.deceasedGender') }}</label>
              <div class="gender-toggle">
                <button
                  type="button"
                  class="gender-btn"
                  :class="{ active: deceasedGender === 'male' }"
                  @click="setDeceasedGender('male')"
                >
                  {{ $t('calculator.male') }}
                </button>
                <button
                  type="button"
                  class="gender-btn"
                  :class="{ active: deceasedGender === 'female' }"
                  @click="setDeceasedGender('female')"
                >
                  {{ $t('calculator.female') }}
                </button>
              </div>
            </div>

            <!-- School of Jurisprudence -->
            <div class="input-section">
              <label class="section-label">{{ $t('calculator.school') }}</label>
              <div class="school-toggle">
                <button
                  type="button"
                  class="school-btn"
                  :class="{ active: school === 'hanafi' }"
                  @click="school = 'hanafi'"
                >
                  {{ $t('calculator.hanafi') }}
                </button>
                <button
                  type="button"
                  class="school-btn"
                  :class="{ active: school === 'shia_ithna_ashari' }"
                  @click="school = 'shia_ithna_ashari'"
                >
                  {{ $t('calculator.shiaIthnaAshari') }}
                </button>
              </div>
            </div>

            <!-- Heir Selection -->
            <div class="input-section">
              <label class="section-label">{{ $t('calculator.selectHeirs') }}</label>
              <HeirInput
                :available-heirs="availableHeirs"
                :heir-counts="heirCounts"
                @increment="incrementHeir"
                @decrement="decrementHeir"
                @toggle="toggleHeir"
              />
            </div>

            <!-- Estate Form -->
            <EstateForm
              :estate-value="estateValue"
              :funeral-expenses="funeralExpenses"
              :debts="debts"
              :unpaid-mahr="unpaidMahr"
              :wasiyyah-amount="wasiyyahAmount"
              @update:estate-value="estateValue = $event"
              @update:funeral-expenses="funeralExpenses = $event"
              @update:debts="debts = $event"
              @update:unpaid-mahr="unpaidMahr = $event"
              @update:wasiyyah-amount="wasiyyahAmount = $event"
            />

            <!-- Calculate Button -->
            <div class="calc-actions">
              <button
                type="button"
                class="calc-btn primary"
                :disabled="!hasHeirs || isCalculating"
                @click="handleCalculate"
              >
                {{ isCalculating ? $t('calculator.calculating') : $t('calculator.calculate') }}
              </button>
              <button
                type="button"
                class="calc-btn ghost"
                @click="handleReset"
              >
                {{ $t('calculator.reset') }}
              </button>
            </div>

            <!-- Error -->
            <div v-if="errorMessage" class="calc-error">
              {{ errorMessage }}
            </div>
          </div>

          <!-- Right: Family Tree + Result Panel -->
          <div class="calc-result-panel">
            <!-- Family Tree (always visible) -->
            <FamilyTree
              :deceased-gender="deceasedGender"
              :heir-counts="heirCounts"
              :result="result"
            />

            <!-- Calculation Result (after calculating) -->
            <template v-if="result">
              <div class="result-divider" />
              <CalculationResult :result="result" />

              <!-- CTA: Get Scholar Verification -->
              <div class="cta-section">
                <div class="cta-card">
                  <h3 class="cta-title">{{ $t('calculator.ctaTitle') }}</h3>
                  <p class="cta-subtitle">{{ $t('calculator.ctaSubtitle') }}</p>

                  <ul class="cta-bullets">
                    <li class="cta-bullet">
                      <span class="cta-bullet-icon">✓</span>
                      <span>{{ $t('calculator.ctaBullet1') }}</span>
                    </li>
                    <li class="cta-bullet">
                      <span class="cta-bullet-icon">✓</span>
                      <span>{{ $t('calculator.ctaBullet2') }}</span>
                    </li>
                    <li class="cta-bullet">
                      <span class="cta-bullet-icon">✓</span>
                      <span>{{ $t('calculator.ctaBullet3') }}</span>
                    </li>
                  </ul>

                  <!-- Authenticated: go directly to case creation -->
                  <button
                    v-if="isAuthenticated"
                    type="button"
                    class="calc-btn primary cta-btn"
                    @click="handleCreateCase"
                  >
                    {{ $t('calculator.getVerification') }}
                  </button>

                  <!-- Anonymous: send via WhatsApp -->
                  <a
                    v-else
                    :href="whatsAppUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="calc-btn primary cta-btn whatsapp-cta"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    {{ $t('calculator.sendViaWhatsApp') }}
                  </a>

                  <p v-if="isAuthenticated" class="cta-login-hint">
                    {{ $t('calculator.loggedInHint') }}
                  </p>
                  <p v-else class="cta-login-hint">
                    {{ $t('calculator.alreadyHaveAccount') }}
                    <router-link to="/login" class="cta-login-link">{{ $t('login.signIn') }}</router-link>
                  </p>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@shared/composables/useAuth.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useCalculator } from '../composables/useCalculator.js'
import HeirInput from '../components/HeirInput.vue'
import CalculationResult from '../components/CalculationResult.vue'
import EstateForm from '../components/EstateForm.vue'
import FamilyTree from '../components/FamilyTree.vue'

const router = useRouter()
const { isAuthenticated } = useAuth()
const { calculate } = useFaraid()
const { locale } = useI18n()

// Language switching
const availableLocales = [
  { code: 'en_IN', label: 'EN', dir: 'ltr' },
  { code: 'hi_IN', label: 'हिं', dir: 'ltr' },
  { code: 'ur_IN', label: 'اردو', dir: 'rtl' },
  { code: 'ta_IN', label: 'தமி', dir: 'ltr' },
]

const currentLocale = computed(() => locale.value)
const isRTL = computed(() => locale.value === 'ur_IN')

const switchLocale = (code) => {
  locale.value = code
  localStorage.setItem('faraid_locale', code)
  document.documentElement.setAttribute('dir', code === 'ur_IN' ? 'rtl' : 'ltr')
  document.documentElement.setAttribute('lang', code.split('_')[0])
}
const {
  deceasedGender,
  school,
  estateValue,
  funeralExpenses,
  debts,
  unpaidMahr,
  wasiyyahAmount,
  result,
  isCalculating,
  heirCounts,
  availableHeirs,
  hasHeirs,
  calculatePayload,
  setDeceasedGender,
  incrementHeir,
  decrementHeir,
  toggleHeir,
  reset,
  saveToSession,
} = useCalculator()

const errorMessage = ref('')

const handleCalculate = async () => {
  try {
    errorMessage.value = ''
    isCalculating.value = true
    const response = await calculate(calculatePayload.value)
    result.value = response.calculation || response
  } catch (error) {
    errorMessage.value = error.message || 'Calculation failed'
  } finally {
    isCalculating.value = false
  }
}

const handleReset = () => {
  reset()
  errorMessage.value = ''
}

// WhatsApp business number (hardcoded for now, settings-based later)
const WHATSAPP_NUMBER = '918925610640'

// Category → display label mapping for WhatsApp message
const heirLabels = {
  husband: 'Husband', wife: 'Wife', son: 'Son', daughter: 'Daughter',
  father: 'Father', mother: 'Mother', sons_son: "Son's Son", sons_daughter: "Son's Daughter",
  fathers_father: "Father's Father", paternal_grandmother: 'Paternal Grandmother',
  maternal_grandmother: 'Maternal Grandmother', full_brother: 'Full Brother',
  full_sister: 'Full Sister', paternal_half_brother: 'Paternal Half-Brother',
  paternal_half_sister: 'Paternal Half-Sister', maternal_half_brother: 'Maternal Half-Brother',
  maternal_half_sister: 'Maternal Half-Sister', full_brothers_son: "Full Brother's Son",
  paternal_half_brothers_son: "Paternal Half-Brother's Son",
  full_paternal_uncle: 'Full Paternal Uncle', paternal_half_uncle: 'Paternal Half-Uncle',
  full_cousin: 'Full Cousin', paternal_half_cousin: 'Paternal Half-Cousin',
}

const formatINR = (val) => Number(val).toLocaleString('en-IN')

const buildWhatsAppMessage = () => {
  const r = result.value
  if (!r) return ''

  const gender = deceasedGender.value === 'male' ? 'Male' : 'Female'
  const schoolName = school.value === 'hanafi' ? 'Hanafi' : 'Shia Ithna Ashari'

  let msg = `*Faraid Calculation — Scholar Review Request*\n`
  msg += `━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`

  // ── INPUTS ──
  msg += `*INPUTS*\n\n`

  msg += `*Deceased:* ${gender}\n`
  msg += `*School:* ${schoolName}\n\n`

  // Heirs (the user's input)
  msg += `*Surviving Heirs:*\n`
  const activeHeirs = Object.entries(heirCounts).filter(([, count]) => count > 0)
  for (const [cat, count] of activeHeirs) {
    const label = heirLabels[cat] || cat
    msg += `  • ${label}: ${count}\n`
  }

  // Estate details
  const ev = Number(estateValue.value) || 0
  const fe = Number(funeralExpenses.value) || 0
  const db = Number(debts.value) || 0
  const mh = Number(unpaidMahr.value) || 0
  const wa = Number(wasiyyahAmount.value) || 0
  const deductionTotal = fe + db + mh + wa

  if (ev > 0) {
    msg += `\n*Estate Details:*\n`
    msg += `  • Total Estate: ₹${formatINR(ev)}\n`
    if (fe > 0) msg += `  • Funeral Expenses: ₹${formatINR(fe)}\n`
    if (db > 0) msg += `  • Outstanding Debts: ₹${formatINR(db)}\n`
    if (mh > 0) msg += `  • Unpaid Mahr: ₹${formatINR(mh)}\n`
    if (wa > 0) msg += `  • Wasiyyah (Bequest): ₹${formatINR(wa)}\n`
    if (deductionTotal > 0) {
      msg += `  • *Total Deductions: ₹${formatINR(deductionTotal)}*\n`
      msg += `  • *Distributable Estate: ₹${formatINR(ev - deductionTotal)}*\n`
    }
  }

  // ── RESULTS ──
  msg += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
  msg += `*RESULTS*\n\n`

  // Share distribution
  if (r.shares && r.shares.length > 0) {
    msg += `*Share Distribution:*\n`
    for (const s of r.shares) {
      if (s.is_blocked) continue
      const label = heirLabels[s.heir_category] || s.heir_category
      const pct = s.share_percentage.toFixed(2)
      const fraction = `${s.share_fraction_num}/${s.share_fraction_den}`
      const perPerson = s.heir_count > 1 ? ` (${s.per_person_pct.toFixed(2)}% each)` : ''
      let line = `  • ${label} × ${s.heir_count} — ${pct}% (${fraction})${perPerson}`
      // Include ₹ amounts if estate was provided
      if (ev > 0 && s.amount_total) {
        line += `\n    ₹${formatINR(s.amount_total.toFixed(0))} total`
        if (s.heir_count > 1 && s.amount_per_person) {
          line += ` (₹${formatINR(s.amount_per_person.toFixed(0))} each)`
        }
      }
      msg += line + `\n`
    }
  }

  // Blocked heirs
  if (r.blocked_heirs && r.blocked_heirs.length > 0) {
    msg += `\n*Blocked Heirs (Hajb):*\n`
    for (const b of r.blocked_heirs) {
      const label = heirLabels[b.category] || b.category
      const blocker = heirLabels[b.blocked_by] || b.blocked_by
      msg += `  • ${label} (${b.count}) — blocked by ${blocker}\n`
    }
  }

  // Awl / Radd
  if (r.awl_applied) {
    msg += `\n⚠️ *Awl Applied* — Total shares exceeded 100%`
    if (r.awl_denominator) msg += ` (denominator increased to ${r.awl_denominator})`
    msg += `, proportionally reduced.\n`
  }
  if (r.radd_applied) {
    msg += `\n⚠️ *Radd Applied* — Surplus`
    if (r.radd_surplus_pct) msg += ` ${r.radd_surplus_pct.toFixed(2)}%`
    msg += ` redistributed to eligible non-spouse heirs.\n`
  }

  // Calculation steps
  if (r.steps && r.steps.length > 0) {
    msg += `\n*Calculation Steps:*\n`
    for (const step of r.steps) {
      msg += `${step}\n`
    }
  }

  // Algorithm version
  if (r.algorithm_version) {
    msg += `\n_Algorithm: ${r.algorithm_version}_\n`
  }

  // ── CTA ──
  msg += `\n━━━━━━━━━━━━━━━━━━━━━━━━━━\n`
  msg += `I would like a qualified scholar (Mufti/Alim) to review and verify this calculation.\n\n`
  msg += `_Calculated on faraid.oneplacehq.com_`
  return msg
}

const whatsAppUrl = computed(() => {
  if (!result.value) return '#'
  const message = buildWhatsAppMessage()
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`
})

// Authenticated users: go directly to case creation
const handleCreateCase = () => {
  saveToSession()
  router.push('/cases/new?fromCalc=true')
}
</script>

<style scoped>
.calculator-page {
  min-height: 100vh;
  background: var(--color-gray-50);
}

/* Header */
.calc-header {
  background: var(--color-white);
  border-bottom: 1px solid var(--color-gray-200);
  position: sticky;
  top: 0;
  z-index: 10;
}

.calc-header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-3) var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.calc-brand {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.calc-logo {
  height: 32px;
  width: auto;
}

.calc-brand-text {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.lang-group {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.lang-hint {
  font-size: var(--font-size-xs);
  color: var(--color-blue);
  white-space: nowrap;
}

.lang-switcher {
  display: flex;
  gap: 2px;
  background: rgba(59, 130, 246, 0.08);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: var(--radius-md);
  padding: 2px;
}

.lang-btn {
  padding: var(--space-1) var(--space-2);
  border: none;
  border-radius: calc(var(--radius-md) - 2px);
  background: transparent;
  color: var(--color-blue);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1.4;
  opacity: 0.7;
}

.lang-btn:hover {
  opacity: 1;
}

.lang-btn.active {
  background: var(--color-blue);
  color: var(--color-white);
  opacity: 1;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.3);
}

.login-link {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  text-decoration: none;
  padding: var(--space-2) var(--space-4);
  border: 1px solid var(--color-blue);
  border-radius: var(--radius-md);
  transition: all 0.15s ease;
}

.login-link:hover {
  background: var(--color-blue);
  color: var(--color-white);
}

/* Main */
.calc-main {
  padding: var(--space-8) var(--space-6);
}

.calc-container {
  max-width: 1200px;
  margin: 0 auto;
}

.calc-intro {
  text-align: center;
  margin-bottom: var(--space-10);
}

.calc-title {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-2) 0;
}

.calc-subtitle {
  font-size: var(--font-size-lg);
  color: var(--color-gray-500);
  margin: 0;
}

/* Two-Column Layout */
.calc-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-8);
  align-items: start;
}

.calc-input-panel,
.calc-result-panel {
  background: var(--color-white);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
  padding: var(--space-6);
}

.calc-result-panel {
  position: sticky;
  top: 80px;
}

/* Input Sections */
.input-section {
  margin-bottom: var(--space-6);
}

.section-label {
  display: block;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  margin-bottom: var(--space-3);
}

/* Gender / School Toggles */
.gender-toggle,
.school-toggle {
  display: flex;
  gap: var(--space-2);
}

.gender-btn,
.school-btn {
  flex: 1;
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--color-gray-300);
  border-radius: var(--radius-md);
  background: var(--color-white);
  color: var(--color-gray-600);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.15s ease;
}

.gender-btn:hover,
.school-btn:hover {
  border-color: var(--color-blue);
}

.gender-btn.active,
.school-btn.active {
  background: var(--color-blue);
  border-color: var(--color-blue);
  color: var(--color-white);
}

/* Actions */
.calc-actions {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.calc-btn {
  padding: var(--space-3) var(--space-6);
  border-radius: var(--radius-md);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
}

.calc-btn.primary {
  flex: 1;
  background: var(--color-blue);
  color: var(--color-white);
}

.calc-btn.primary:hover:not(:disabled) {
  opacity: 0.9;
}

.calc-btn.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.calc-btn.ghost {
  background: transparent;
  color: var(--color-gray-500);
  border: 1px solid var(--color-gray-300);
}

.calc-btn.ghost:hover {
  background: var(--color-gray-50);
}

/* Error */
.calc-error {
  margin-top: var(--space-4);
  padding: var(--space-3) var(--space-4);
  background: var(--color-error-bg);
  border: 1px solid var(--color-error);
  border-radius: var(--radius-md);
  color: var(--color-error);
  font-size: var(--font-size-sm);
}

/* Result divider between tree and calculation */
.result-divider {
  height: 1px;
  background: var(--color-gray-200);
  margin: var(--space-6) 0;
}

/* CTA Section */
.cta-section {
  margin-top: var(--space-6);
}

.cta-card {
  background: rgba(59, 130, 246, 0.06);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-inline-start: 4px solid var(--color-blue);
  border-radius: var(--radius-md);
  padding: var(--space-6);
}

.cta-title {
  margin: 0 0 var(--space-2) 0;
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-900);
}

.cta-subtitle {
  margin: 0 0 var(--space-4) 0;
  font-size: var(--font-size-sm);
  color: var(--color-gray-600);
  line-height: 1.5;
}

.cta-bullets {
  list-style: none;
  margin: 0 0 var(--space-5) 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.cta-bullet {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: var(--font-size-sm);
  color: var(--color-gray-700);
  line-height: 1.4;
}

.cta-bullet-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  background: rgba(59, 130, 246, 0.12);
  color: var(--color-blue);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: var(--font-weight-bold);
  margin-top: 1px;
}

.cta-btn {
  background: var(--color-blue) !important;
  color: var(--color-white) !important;
  width: 100%;
}

.whatsapp-cta {
  background: #25D366 !important;
  color: var(--color-white) !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  text-decoration: none;
  font-weight: var(--font-weight-semibold);
}

.whatsapp-cta:hover {
  background: #1FAD4D !important;
}

.cta-login-hint {
  margin: var(--space-3) 0 0 0;
  font-size: var(--font-size-xs);
  color: var(--color-gray-500);
  text-align: center;
}

.cta-login-link {
  color: var(--color-blue);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 900px) {
  .calc-layout {
    grid-template-columns: 1fr;
  }

  .calc-result-panel {
    position: static;
  }
}

@media (max-width: 640px) {
  .calc-main {
    padding: var(--space-4) var(--space-3);
  }

  .calc-title {
    font-size: var(--font-size-2xl);
  }

  .calc-subtitle {
    font-size: var(--font-size-base);
  }

  .calc-input-panel,
  .calc-result-panel {
    padding: var(--space-4);
  }

  .lang-hint {
    display: none;
  }
}

/* RTL (Urdu) — direction: rtl auto-reverses flex/grid, so minimal overrides needed */
[dir="rtl"] .login-link {
  /* ensure login button text doesn't inherit odd bidi from mixed-script content */
  unicode-bidi: plaintext;
}
</style>
